import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './ApiDocs.css';
import './OptionROI.css';

function OptionROI() {
  const [symbols, setSymbols] = useState([]);
  const [symbol, setSymbol] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSymbols();
  }, []);

  const fetchSymbols = async () => {
    try {
      const res = await axios.get(`${API_URL}/portfolio/option-roi/`);
      const list = res.data?.symbols || [];
      setSymbols(list);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load symbols');
      setSymbols([]);
    }
  };

  const loadStored = async (sym) => {
    const s = (sym || symbol || '').trim().toUpperCase();
    if (!s) {
      setError('Enter a symbol');
      return;
    }
    setSymbol(s);
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await axios.get(`${API_URL}/portfolio/option-roi/`, { params: { symbol: s } });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Load failed');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getData = async () => {
    const sym = (symbol || '').trim().toUpperCase();
    if (!sym) {
      setError('Enter a symbol');
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await axios.post(`${API_URL}/portfolio/option-roi/`, { symbol: sym });
      setData(res.data);
      await fetchSymbols();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Get data failed');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="option-roi-page">
      <aside className="option-roi-sidebar">
        <h3>Stored</h3>
        <p className="sidebar-hint">Symbols we have in the database. Click to load.</p>
        {symbols.length === 0 && !loading && (
          <p className="sidebar-hint">No symbols yet. Enter one and click Get data.</p>
        )}
        <div className="option-roi-sidebar-badges">
          {symbols.map((s) => (
            <button
              key={s.symbol}
              type="button"
              className="option-roi-symbol-btn"
              onClick={() => loadStored(s.symbol)}
              disabled={loading}
            >
              {s.symbol}
            </button>
          ))}
        </div>
      </aside>

      <main className="option-roi-main">
        <div className="api-docs-content">
          <div className="endpoint-section">
            <h2>Option ROI (Covered call chain)</h2>
            <p className="endpoint-description">
              Get data from marketdata.app and store it. Stored symbols are in the left sidebar.
            </p>

            <div className="method-card">
              <label htmlFor="option-roi-symbol">Symbol</label>
              <input
                id="option-roi-symbol"
                type="text"
                className="option-roi-input"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                placeholder="e.g. AAPL"
              />
              <button type="button" className="option-roi-get-btn" onClick={getData} disabled={loading}>
                Get data
              </button>
            </div>

            {error && (
              <div className="method-card error">
                {error}
              </div>
            )}

            {loading && <p>Loading…</p>}

            {data && !loading && (
              <div className="method-card option-roi-result">
                <h4>Result — {data.symbol}</h4>
                <div className="option-roi-summary">
                  <span>Stock price: <strong>${Number(data.stock_price).toFixed(2)}</strong></span>
                  {data.volatility_risk != null && (
                    <span>
                      Volatility: <strong>{data.volatility_risk}</strong>
                      {data.avg_iv != null && ` (IV ${(Number(data.avg_iv) * 100).toFixed(1)}%)`}
                    </span>
                  )}
                  {data.option_data_updated_at && (
                    <span className="option-roi-meta">Data: {data.option_data_updated_at}</span>
                  )}
                </div>
                {(data.expirations || []).map((exp) => (
                  <div key={exp.expiration_date} className="option-roi-expiry-block">
                    <h5>
                      Expiry {exp.expiration_date}
                      {exp.days_to_expiration != null && (
                        <span className="option-roi-days"> ({exp.days_to_expiration} days)</span>
                      )}
                    </h5>
                    <table className="option-roi-table">
                      <thead>
                        <tr>
                          <th>Strike</th>
                          <th>Premium</th>
                          <th>Delta</th>
                          <th>Volatility</th>
                          <th>ROI % 30d</th>
                          <th>ROI % assigned 30d</th>
                          <th>Premium assigned</th>
                          <th className="option-roi-th-risk">
                            <span>Risk</span>
                            <span className="option-roi-risk-info-wrap">
                              <span
                                className="option-roi-risk-info-icon"
                                title="Volatility risk from implied volatility (IV): Low = IV &lt; 30%, Medium = 30–60%, High = IV ≥ 60%. Based on average IV of the 6 strikes shown (first expiry)."
                                aria-label="How risk is determined"
                              >
                                ⓘ
                              </span>
                              <span className="option-roi-risk-tooltip">
                                Volatility risk from implied volatility (IV): Low = IV &lt; 30%, Medium = 30–60%, High = IV ≥ 60%. Based on average IV of the 6 strikes shown (first expiry).
                              </span>
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(exp.short_calls || []).map((call, i) => {
                          const iv = call.iv != null ? Number(call.iv) : null;
                          const risk = call.risk ?? (iv != null ? (iv < 0.30 ? 'low' : iv < 0.60 ? 'medium' : 'high') : null);
                          return (
                            <tr key={i}>
                              <td>${Number(call.strike).toFixed(2)}</td>
                              <td>${Number(call.premium).toFixed(2)}</td>
                              <td>{call.delta != null ? Number(call.delta).toFixed(4) : '—'}</td>
                              <td>{call.iv != null ? `${(Number(call.iv) * 100).toFixed(1)}%` : '—'}</td>
                              <td>{call.roi_premium_only_30d_pct != null ? `${call.roi_premium_only_30d_pct}%` : '—'}</td>
                              <td>{call.roi_if_assigned_30d_pct != null ? `${call.roi_if_assigned_30d_pct}%` : '—'}</td>
                              <td>{call.premium_assigned != null ? `$${Number(call.premium_assigned).toFixed(2)}` : (data.stock_price != null && call.strike != null && call.premium != null ? `$${(100 * (Number(call.strike) - Number(data.stock_price) + Number(call.premium))).toFixed(2)}` : '—')}</td>
                              <td>
                                {risk ? (
                                  <span className={`option-roi-risk-badge option-roi-risk-${risk}`}>
                                    {risk.charAt(0).toUpperCase() + risk.slice(1)}
                                  </span>
                                ) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                        {(!exp.short_calls || exp.short_calls.length === 0) && (
                          <tr><td colSpan={8}>No strikes</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default OptionROI;
